import * as qs from 'query-string';
import * as React from 'react';
import { RouteComponentProps } from 'react-router';

import { CILNotificationSelection, CILNotificationViewModel } from '@services/CILNotificationService';

import { CILNotificationCIL } from './cil';
import { CILNotificationReplacement } from './replacement';

export enum CILNotificationComponentPage {
  CIL,
  Confirmation,
  ConfirmationError,
  Landing,
  Replacement
}

// Defining the properties the component expects
// Consider this the "setup" of the component
// All properties should be readonly
export interface CILNotificationComponentProperties extends RouteComponentProps<{}> {
  readonly getViewModel: (swoTrackingCILOfferId: number, contractId: number, token: string) => void;
  readonly gotoPage: (page: CILNotificationComponentPage) => void;
  readonly isLoading: boolean;
  readonly page: CILNotificationComponentPage;
  readonly submitSelection: (selection: CILNotificationSelection, swoTrackingCILOfferId: number, token: string) => void;
  readonly swoTrackingCILOfferId?: number;
  readonly token?: string;
  readonly viewModel?: CILNotificationViewModel;
  readonly warningMessage: string;
}

// Component definition
export class CILNotificationComponent extends React.Component<CILNotificationComponentProperties> {
  private static formatMoney(input: number): string {
    if (input === 0) {
      return '$0.00';
    }
    return input.toFixed(2).replace(/(\d)(?=(\d{3})+\.)/g, '$1,');
  }

  private static renderFooter() {
    return (
      <div className="text-center">
        <h3>Go online - stay informed</h3>
        <p>Get service updates, contract coverage, and more online at homewaranty.com</p>
        <a href="https://www.homewarranty.com/">
          <button className="btn btn-primary">Account Login</button>
        </a>
      </div>
    );
  }

  componentWillMount() {
    const queryParameters: any = qs.parse(this.props.location.search);
    const swoTrackingCILOfferId: number = parseInt(queryParameters.swoTrackingCILOfferId, 10);
    const contractId: number = parseInt(queryParameters.contractId, 10);
    const token: string = queryParameters.key ? queryParameters.key.toString() : undefined;
    if (swoTrackingCILOfferId && contractId && token) {
      this.props.getViewModel(swoTrackingCILOfferId, contractId, token);
    }
  }

  render() {
    if (this.props.isLoading) {
      return (
        <>
          <p className="text-center">
            <strong>Saving...</strong>
          </p>
          <br />
          {CILNotificationComponent.renderFooter()}
        </>
      );
    }

    if (!this.props.viewModel) {
      return (
        <>
          <p className="text-center">
            <strong>{this.props.warningMessage}</strong>
          </p>
          <br />
          {CILNotificationComponent.renderFooter()}
        </>
      );
    }

    let contentMarkup: JSX.Element = <></>;
    switch (this.props.page) {
      case CILNotificationComponentPage.CIL:
        contentMarkup = (
          <CILNotificationCIL
            leave={() => this.props.gotoPage(CILNotificationComponentPage.Replacement)}
            submitCIL={() =>
              this.props.submitSelection(
                CILNotificationSelection.CashInLieu,
                this.props.swoTrackingCILOfferId!,
                this.props.token!
              )
            }
            viewModel={this.props.viewModel}
          />
        );
        break;
      case CILNotificationComponentPage.Confirmation:
        contentMarkup = (
          <div className="row justify-content-md-center">
            <div className="col col-md-12">
              <div className="alert alert-default" role="alert">
                <h3 className="alert-heading">Confirmation:</h3>
                <p>Thank you for your selection! You will receive an email confirmation shortly.</p>
              </div>
            </div>
          </div>
        );
        break;
      case CILNotificationComponentPage.ConfirmationError:
        contentMarkup = (
          <div className="row justify-content-md-center">
            <div className="col col-md-12">
              <div className="alert alert-danger" role="alert">
                <h3 className="alert-heading">Confirmation:</h3>
                <p>There was an error processing your selection; please try again later.</p>
              </div>
            </div>
          </div>
        );
        break;
      case CILNotificationComponentPage.Replacement:
        contentMarkup = (
          <CILNotificationReplacement
            leave={() => this.props.gotoPage(CILNotificationComponentPage.CIL)}
            submitReplacement={() =>
              this.props.submitSelection(
                CILNotificationSelection.Replacement,
                this.props.swoTrackingCILOfferId!,
                this.props.token!
              )
            }
            viewModel={this.props.viewModel}
          />
        );
        break;
      default:
        contentMarkup = this.renderLanding(this.props.viewModel);
        break;
    }

    return (
      <div className="row center-xs">
        <div className="col-xl-6 col-lg-10">
          {this.renderCommon(this.props.viewModel)}
          <br />
          {contentMarkup}
          <br />
          <hr />
          {CILNotificationComponent.renderFooter()}
        </div>
      </div>
    );
  }

  private renderCommon(viewModel: CILNotificationViewModel) {
    return (
      <>
        <div className="row justify-content-md-center">
          <div className="col col-md-12">
            <h3 className="text-center">
              <strong>Action Request</strong>
            </h3>
          </div>
        </div>
        <div className="row">
          <div className="col col-xs-6 text-left">
            <p>Fidelity National Home Warranty</p>
            <p>P.O. Box 8127</p>
            <p>Walnut Creek, CA 94596</p>
          </div>
          <div className="col col-xs-6 text-right">
            <p>Order: {viewModel.Service_Request_Num}</p>
            <p>Contract: {viewModel.Contract_Num}</p>
          </div>
        </div>
      </>
    );
  }

  private renderLanding(viewModel: CILNotificationViewModel) {
    let cilAmount: number = viewModel.Cash_In_Lieu_Am ? parseFloat(viewModel.Cash_In_Lieu_Am) : 0;

    const cilButtonText =
      viewModel.CIL_Only_Fl && viewModel.CIL_Only_Fl === true ? 'Accept Cash-in-Lieu' : 'Choose this option';

    return (
      <>
        <p className="text-center">Great news, {viewModel.Customer_Nm}!</p>
        {viewModel.CIL_Only_Fl ? (
          <div className="text-left">
            <p>
              We are offering you cash-in-lieu of replacement for the following reason: {viewModel.CIL_Only_Reason_Ds}
              {!viewModel.CIL_Only_Reason_Ds!.endsWith('.') && '.'}
              The total amount includes the cost of the unit, tax and Fidelity National Home Warranty’s cost for
              delivery, basic installation and disposal of the old unit.
            </p>
            <ul className="list-unstyled">
              <li>Offer amount: ${CILNotificationComponent.formatMoney(cilAmount)}</li>
            </ul>
            <h4>Benefits to Cash-In-Lieu</h4>
            <ul>
              <li>
                <b>
                  <u>Allows you to:</u>
                </b>
              </li>
              <ul>
                <li>Purchase any appliance of your choice</li>
                <li>Take advantage of sales/special deals that may not be available to FNHW.</li>
                <li>
                  Control delivery of the appliance on{' '}
                  <b>
                    <i>your</i>
                  </b>{' '}
                  schedule.
                </li>
              </ul>
            </ul>
          </div>
        ) : (
          <p className="text-center">
            Purchasing has evaluated potential options for the {viewModel.Appliance_Type} service request at{' '}
            {viewModel.Contract_Address1}, {viewModel.Contract_City}, {viewModel.Contract_State}{' '}
            {viewModel.Contract_Zip} and are prepared for you to choose one of the options below:
          </p>
        )}
        <br />
        <div className="row around-xs">
          {!viewModel.CIL_Only_Fl && (
            <div className="col col-6">
              <div className="text-center">
                <h4>
                  <strong>REPLACEMENT:</strong>
                </h4>
                <h1 className="fas fa-box display-block" />
                <button
                  className="btn btn-secondary"
                  onClick={() => this.props.gotoPage(CILNotificationComponentPage.Replacement)}
                >
                  Choose this option
                </button>
              </div>
            </div>
          )}
          <div className="col col-6">
            <div className="text-center">
              <h4>
                <strong>CASH IN LIEU OF REPLACEMENT:</strong>
              </h4>
              <h1>
                <strong>${CILNotificationComponent.formatMoney(cilAmount)}</strong>
              </h1>
              <button
                className="btn btn-secondary"
                onClick={() => this.props.gotoPage(CILNotificationComponentPage.CIL)}
              >
                {cilButtonText}
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }
}



// WEBPACK FOOTER //
// ./src/cil-notification/_components/cil-notification.component.tsx